import React, { useState } from "react";
import { X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { reportsApi } from "../lib/events";
import { extractError } from "../lib/api";

/**
 * ResendTicketModal — re-send an existing ticket to its current attendee email.
 * Never edits attendee/buyer data, order or check-in state. The button is
 * disabled while sending so accidental double-clicks cannot fire twice.
 */
export function ResendTicketModal({ eventId, ticket, onClose, onResent }) {
  const [busy, setBusy] = useState(false);
  if (!ticket) return null;

  // When PII is masked the email value is null but has_attendee_email tells us
  // whether a target exists on file.
  const emailKnown = ticket.attendee_email || null;
  const hasEmail = ticket.has_attendee_email || !!emailKnown;
  const target = emailKnown || "the attendee's email on file";

  const onConfirm = async () => {
    if (busy) return; // double-click guard
    setBusy(true);
    try {
      const res = await reportsApi.resendTicket(eventId, ticket.id);
      toast.success(res.message || "Ticket resent successfully.");
      onResent?.(res);
      onClose?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="resend-ticket-modal"
    >
      <div
        className="bg-white border-2 border-[var(--gt-ink)] shadow-[8px_8px_0px_0px_var(--gt-ink)] w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-[var(--gt-ink)]">
          <div>
            <div className="gt-label flex items-center gap-1"><Send size={13} /> Resend ticket</div>
            <h3 className="font-heading text-2xl font-black tracking-tighter mt-1">Send again</h3>
            <div className="text-xs text-[var(--gt-ink-3)] mt-1 font-mono">Ticket {ticket.code}</div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"
            data-testid="resend-ticket-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5" data-testid="resend-ticket-body">
          {!hasEmail ? (
            <p className="text-sm text-[var(--gt-ink-2)]" data-testid="resend-ticket-no-email">
              This ticket does not have an attendee email. Edit the attendee
              details first.
            </p>
          ) : (
            <p className="text-sm text-[var(--gt-ink-2)]">
              Resend this ticket to <strong>{target}</strong>? The same ticket,
              QR code, seat and check-in status stay unchanged.
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="gt-btn-secondary"
              data-testid="resend-ticket-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || !hasEmail}
              className="gt-btn-primary inline-flex items-center gap-2"
              data-testid="resend-ticket-confirm"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Resend ticket
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResendTicketModal;
